// src/oura/components/client/asks.ts
//
// Every question a pack has put to the client, sorted by who moves next. Pure
// functions over the approval layer's own lists, so the Questions to the
// client card, the page sentence and the drawer all read one answer.
//
// What the client started (changes, calls) is the Requests card, read in
// changeRequests.ts. Drafts are not questions: nothing in a draft pack has
// reached the client.

import type { Pack, PackItem, Thread } from "@service/aeo/clientReview";
import { DOC_STATUS, type Tone } from "@oura/ui";

export interface Ask {
    item: PackItem;
    pack: Pack;
    thread: Thread | null;
    /** The client suggested new wording that nobody has accepted yet. */
    hasEdit: boolean;
    /** A staff notification about this ask is still unread (the bell's "new"). */
    fresh: boolean;
}

export interface AskGroups {
    /** The client replied in the question's thread: our move. */
    needsUs: Ask[];
    /** Sent, and waiting on the client. */
    waiting: Ask[];
    /** Decided: approved, answered, deferred, or changes requested and already handled. */
    answered: Ask[];
}

/**
 * Whether a thread waits on us. The same test the old Inbox used: the item is
 * marked as waiting on staff, or the conversation is open and the client
 * spoke last.
 */
function waitsOnUs(item: PackItem, thread: Thread | null): boolean {
    if (!thread) return false;
    if (item.waitingOn === "staff") return true;
    const clientSpoke = thread.messages.some((m) => m.authorKind === "client" && (m.kind === "message" || m.kind === "suggested_edit"));
    return thread.status === "open" && clientSpoke && thread.messages[thread.messages.length - 1]?.authorKind === "client";
}

/** The client suggested new wording in this thread that nobody has accepted yet. */
export function hasOpenEdit(thread: Thread | null | undefined): boolean {
    return !!thread?.messages.some((m) => m.suggestedEdit && !m.suggestedEdit.accepted);
}

const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);

export function groupAsks(packs: Pack[], threads: Thread[], unreadItemIds: ReadonlySet<string>): AskGroups {
    const threadByItem = new Map(threads.map((t) => [t.itemId, t] as const));
    const groups: AskGroups = { needsUs: [], waiting: [], answered: [] };
    for (const pack of packs) {
        if (pack.status === "draft") continue;
        for (const item of pack.items) {
            const thread = threadByItem.get(item.id) ?? null;
            const ask: Ask = {
                item,
                pack,
                thread,
                hasEdit: hasOpenEdit(thread),
                fresh: unreadItemIds.has(item.id),
            };
            if (waitsOnUs(item, thread)) groups.needsUs.push(ask);
            // Pending, or reopened by a question of ours: either way the
            // client moves next. The same rule as the staff summary counts.
            else if (item.decision === "pending" || item.waitingOn === "client") groups.waiting.push(ask);
            else groups.answered.push(ask);
        }
    }
    // Late first, then the longest waiting. A late count only exists where the
    // server sent one; without it, the oldest ask leads.
    groups.waiting.sort((a, b) => (b.item.lateDays ?? 0) - (a.item.lateDays ?? 0) || time(a.pack.publishedAt) - time(b.pack.publishedAt));
    groups.answered.sort((a, b) => time(b.item.decidedAt) - time(a.item.decidedAt));
    groups.needsUs.sort((a, b) => time(lastMessageAt(b)) - time(lastMessageAt(a)));
    return groups;
}

function lastMessageAt(ask: Ask): string | null {
    const m = ask.thread?.messages;
    return m && m.length > 0 ? m[m.length - 1].at : null;
}

/** Whole days since `iso`, for "waiting 9 days". Elapsed time only; never a deadline. */
export function daysSince(iso: string | null | undefined, now: number = Date.now()): number | null {
    const t = time(iso);
    if (!t) return null;
    return Math.max(0, Math.floor((now - t) / 86_400_000));
}

/**
 * The one status word on a question. Staff vocabulary (D8), in the Documents
 * board's words (DOC_STATUS) so the Client tab says each state one way: the
 * decision itself once it is made, "Waiting on client" while it is theirs,
 * and while it is ours, "Needs us", or the kind of reply owed when there is
 * one (a suggested edit, changes requested).
 */
export function askStatus(ask: Ask, waitsOnUsNow: boolean): { label: string; tone: Tone } {
    const { item, pack } = ask;
    if (pack.status === "draft") return { label: "Draft", tone: "gray" };
    if (waitsOnUsNow) {
        if (ask.hasEdit) return { label: "Suggested edit", tone: "amber" };
        if (item.decision === "changes_requested") return { label: "Changes requested", tone: "rose" };
        return DOC_STATUS.needs_us.staff;
    }
    if (item.decision === "pending" || item.waitingOn === "client") return DOC_STATUS.waiting_on_client.staff;
    if (item.decision === "approved") return { label: "Approved", tone: "green" };
    if (item.decision === "answered") return { label: "Answered", tone: "green" };
    if (item.decision === "deferred") return { label: "Deferred", tone: "gray" };
    return { label: "Changes requested", tone: "rose" };
}
